import React, { useState, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartLine,
  faBullseye,
  faTasks,
  faPlus,
  faUser,
  faArrowRight,
  faChevronDown,
  faChevronUp,
  faEdit,
  faTrash,
  faDownload,
  faFilter,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

// -------------------- Refactored MSMEGoalBoardHome.jsx --------------------
export default function MSMEGoalBoardHome() {
  // Dummy initial data
  const initialGoals = [
    {
      id: 1,
      title: "Improve MSME Outreach",
      description: "Increase engagement and registrations under MSME program.",
      progress: 75,
      quarter: "Q4-2025",
      status: "On Track",
      kras: [
        {
          id: 101,
          title: "Digital Campaign Reach",
          weight: 40,
          progress: 80,
          metrics: [
            { id: 1001, name: "Total Impressions", value: 8200, target: 10000, unit: "visits" },
            { id: 1002, name: "Registrations", value: 560, target: 800, unit: "nos" },
          ],
        },
        {
          id: 102,
          title: "Partner Collaborations",
          weight: 60,
          progress: 68,
          metrics: [
            { id: 1003, name: "New Partners Onboarded", value: 12, target: 20, unit: "partners" },
          ],
        },
      ],
    },
    {
      id: 2,
      title: "Enhance Customer Experience",
      description: "Improve NPS and reduce service turnaround time.",
      progress: 60,
      quarter: "Q4-2025",
      status: "At Risk",
      kras: [
        {
          id: 201,
          title: "Service Delivery",
          weight: 50,
          progress: 60,
          metrics: [
            { id: 2001, name: "Avg Resolution Time (hrs)", value: 5, target: 3, unit: "hrs" },
          ],
        },
        {
          id: 202,
          title: "Customer Feedback",
          weight: 50,
          progress: 48,
          metrics: [
            { id: 2002, name: "NPS Score", value: 78, target: 90, unit: "score" },
          ],
        },
      ],
    },
  ];

  const [goals, setGoals] = useState(initialGoals);
  const [expandedGoalId, setExpandedGoalId] = useState(null);
  const [expandedKraIds, setExpandedKraIds] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingGoalId, setEditingGoalId] = useState(null);
  const [currentGoal, setCurrentGoal] = useState({ title: "", description: "", quarter: "", kras: [] });
  const [filter, setFilter] = useState("all");

  // Helpers
  const avgProgress = useMemo(() => {
    if (!goals.length) return 0;
    return Math.round(goals.reduce((s, g) => s + (g.progress || 0), 0) / goals.length);
  }, [goals]);

  const topObjective = useMemo(() => (goals[0] ? goals[0].title : "-"), [goals]);

  // Chart data for goal progress bar chart
  const chartData = useMemo(() => ({
    labels: goals.map((g) => g.title),
    datasets: [
      {
        label: "Progress (%)",
        data: goals.map((g) => g.progress),
        backgroundColor: "rgba(59, 130, 246, 0.5)",
        borderColor: "rgba(59, 130, 246, 1)",
        borderWidth: 1,
      },
    ],
  }), [goals]);

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: "Goal Progress Overview",
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
      },
    },
  };

  // Toggle expand goal
  const toggleGoal = (id) => setExpandedGoalId(expandedGoalId === id ? null : id);

  // Toggle KRA
  const toggleKra = (kraId) => {
    setExpandedKraIds((prev) => 
      prev.includes(kraId) ? prev.filter((i) => i !== kraId) : [...prev, kraId]
    );
  };

  // Update progress for a metric and recalculate KRA and goal progress in one go
  const updateMetric = (goalId, kraId, metricId, newValue) => {
    setGoals((prev) =>
      prev.map((g) => {
        if (g.id !== goalId) return g;
        const updatedKras = g.kras.map((k) => {
          if (k.id !== kraId) return k;
          const updatedMetrics = k.metrics.map((m) => 
            m.id === metricId ? { ...m, value: newValue } : m
          );
          const kraProgress = Math.round(
            updatedMetrics.reduce((s, m) => s + Math.min(100, Math.round((m.value / m.target) * 100)), 0) / 
            (updatedMetrics.length || 1)
          );
          return { ...k, metrics: updatedMetrics, progress: kraProgress };
        });

        // Recalculate goal progress weighted
        const totalWeight = updatedKras.reduce((s, k) => s + (k.weight || 0), 0) || updatedKras.length;
        const weightedProgress = updatedKras.reduce(
          (s, k) => s + (k.progress || 0) * ((k.weight || 0) / totalWeight), 0
        );
        const goalProgress = Math.round(weightedProgress);

        return { ...g, kras: updatedKras, progress: goalProgress };
      })
    );
  };

  // Add a new dummy metric to KRA
  const addSampleMetric = (goalId, kraId) => {
    setGoals((prev) =>
      prev.map((g) => {
        if (g.id !== goalId) return g;
        const nextMetricId = Math.max(0, ...g.kras.flatMap((k) => k.metrics.map((m) => m.id))) + 1;
        return {
          ...g,
          kras: g.kras.map((k) => {
            if (k.id !== kraId) return k;
            const newMetric = { 
              id: nextMetricId, 
              name: "New Metric", 
              value: 0, 
              target: 100, 
              unit: "unit" 
            };
            const updatedMetrics = [...k.metrics, newMetric];
            const kraProgress = Math.round(
              updatedMetrics.reduce((s, m) => s + Math.min(100, Math.round((m.value / m.target) * 100)), 0) / 
              (updatedMetrics.length || 1)
            );
            return { ...k, metrics: updatedMetrics, progress: kraProgress };
          }),
        };
      })
    );
    // Trigger goal recalc (handled in map above if needed, but since no change to weights, optional)
  };

  // Open modal for add/edit
  const openModal = (goal = null) => {
    if (goal) {
      setIsEditing(true);
      setEditingGoalId(goal.id);
      setCurrentGoal({ ...goal });
    } else {
      setIsEditing(false);
      setEditingGoalId(null);
      setCurrentGoal({ title: "", description: "", quarter: "", kras: [] });
    }
    setShowModal(true);
  };

  // Save goal (add or update)
  const saveGoal = () => {
    if (!currentGoal.title) {
      alert("Please provide a goal title.");
      return;
    }

    if (isEditing) {
      // Update existing goal
      setGoals((prev) =>
        prev.map((g) => (g.id === editingGoalId ? { ...currentGoal } : g))
      );
    } else {
      // Add new goal
      const newId = Math.max(0, ...goals.map((g) => g.id)) + 1;
      const goalToAdd = {
        ...currentGoal,
        id: newId,
        progress: 0, // Will be calculated if KRAs present
        status: "Active",
      };
      // If KRAs provided, calculate initial progress
      if (goalToAdd.kras.length > 0) {
        const totalWeight = goalToAdd.kras.reduce((s, k) => s + (k.weight || 0), 0) || goalToAdd.kras.length;
        const weightedProgress = goalToAdd.kras.reduce(
          (s, k) => s + (k.progress || 0) * ((k.weight || 0) / totalWeight), 0
        );
        goalToAdd.progress = Math.round(weightedProgress);
      }
      setGoals((prev) => [goalToAdd, ...prev]);
    }

    setShowModal(false);
  };

  // Remove goal
  const removeGoal = (id) => {
    if (!confirm("Delete this goal?")) return;
    setGoals((prev) => prev.filter((g) => g.id !== id));
  };

  // Export JSON
  const exportJSON = () => {
    const blob = new Blob([JSON.stringify(goals, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "goalboard_export.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  // Filtered goals
  const displayedGoals = useMemo(() => {
    if (filter === "all") return goals;
    return goals.filter((g) => 
      (filter === "ontrack" ? g.status === "On Track" : g.status === "At Risk")
    );
  }, [goals, filter]);

  // Modal handlers for currentGoal updates
  const updateCurrentGoalField = (field, value) => {
    setCurrentGoal((prev) => ({ ...prev, [field]: value }));
  };

  const updateCurrentKraTitle = (value) => {
    setCurrentGoal((prev) => ({
      ...prev,
      kras: [{ 
        id: 9999, 
        title: value || "New KRA", 
        weight: 50, 
        progress: 0, 
        metrics: prev.kras[0]?.metrics || [{ id: 99991, name: prev.kras[0]?.metrics?.[0]?.name || "Metric A", value: 0, target: 100, unit: "unit" }] 
      }]
    }));
  };

  const updateCurrentMetricName = (value) => {
    setCurrentGoal((prev) => ({
      ...prev,
      kras: [{
        ...prev.kras[0],
        metrics: [{ 
          ...prev.kras[0]?.metrics?.[0], 
          name: value || "Metric A" 
        }]
      }]
    }));
  };

  // UI ------------------------------------------------------
  return (
    <div className="min-h-screen bg-gray-950 text-gray-100 p-4 sm:p-6">
      {/* Header */}
      <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <FontAwesomeIcon icon={faBullseye} className="text-green-400 text-2xl" />
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">Goal Board</h1>
            <div className="text-xs text-gray-400">Your unified KRA • KPI • OKR workspace</div>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 w-full sm:w-auto">
          <button
            onClick={() => openModal()}
            className="bg-blue-600 hover:bg-blue-700 py-2 rounded-lg text-sm flex items-center justify-center gap-2"
          >
            <FontAwesomeIcon icon={faPlus} />
            <span className="hidden sm:inline">Add Goal</span>
          </button>

          <button
            onClick={exportJSON}
            className="bg-gray-800 hover:bg-gray-700 py-2 rounded-lg text-sm flex items-center justify-center gap-2"
          >
            <FontAwesomeIcon icon={faDownload} />
            <span className="hidden sm:inline">Export</span>
          </button>

          <button
            onClick={() => setFilter("all")}
            className={`py-2 rounded-lg text-sm ${filter === "all" ? "bg-gray-700" : "bg-gray-800 hover:bg-gray-700"}`}
          >
            All
          </button>

          <button
            onClick={() => setFilter("ontrack")}
            className={`py-2 rounded-lg text-sm ${filter === "ontrack" ? "bg-gray-700" : "bg-gray-800 hover:bg-gray-700"}`}
          >
            On Track
          </button>
        </div>
      </header>

      {/* Summary */}
      <section className="bg-gray-900/60 rounded-2xl p-4 mb-6 border border-gray-800">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-400">Overall Progress</p>
            <h2 className="text-3xl font-bold text-blue-400">{avgProgress}%</h2>
            <div className="text-xs text-gray-400 mt-1">Top objective: {topObjective}</div>
          </div>

          <div className="space-y-2 text-right">
            <div className="text-sm text-gray-400">Goals</div>
            <div className="text-xl font-bold">{goals.length}</div>
            <div className="text-xs text-gray-400">Filter: {filter}</div>
          </div>
        </div>

        <div className="w-full h-3 bg-gray-800 rounded mt-4 overflow-hidden">
          <div className="h-3 bg-blue-500" style={{ width: `${avgProgress}%` }} />
        </div>
      </section>

      {/* Progress Chart */}
      {goals.length > 0 && (
        <section className="bg-gray-900/60 rounded-2xl p-4 mb-6 border border-gray-800">
          <Bar options={chartOptions} data={chartData} />
        </section>
      )}

      {/* Goals list */}
      <div className="space-y-4">
        {displayedGoals.map((goal) => (
          <div key={goal.id} className="bg-gray-900/70 rounded-2xl border border-gray-800 p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-green-300">{goal.title}</h3>
                    <div className="text-xs text-gray-400">{goal.description}</div>
                    <div className="text-xs text-gray-400 mt-1">
                      {goal.quarter} • <span className={goal.status === 'At Risk' ? 'text-yellow-400' : 'text-green-400'}>{goal.status}</span>
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="text-sm text-gray-400">Overall</div>
                    <div className="text-2xl font-bold text-blue-400">{goal.progress}%</div>
                  </div>
                </div>

                {/* KRAs */}
                <div className="mt-3 space-y-3">
                  {goal.kras.map((k) => (
                    <div key={k.id} className="bg-gray-800/60 rounded-lg p-3 border border-gray-700">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-gray-200">{k.title}</h4>
                            <div className="text-xs text-gray-400">• {k.weight}%</div>
                          </div>
                          <div className="w-full h-2 bg-gray-700 rounded mt-2 overflow-hidden">
                            <div className="h-2 bg-blue-500" style={{ width: `${k.progress}%` }} />
                          </div>
                        </div>

                        <div className="ml-3 flex items-center gap-2">
                          <button
                            onClick={() => toggleKra(k.id)}
                            className="p-2 bg-gray-800 rounded-md"
                            title="Toggle metrics"
                          >
                            <FontAwesomeIcon icon={expandedKraIds.includes(k.id) ? faChevronUp : faChevronDown} />
                          </button>
                          <button
                            onClick={() => addSampleMetric(goal.id, k.id)}
                            className="p-2 bg-gray-800 rounded-md"
                            title="Add sample metric"
                          >
                            <FontAwesomeIcon icon={faPlus} />
                          </button>
                        </div>
                      </div>

                      {/* Metrics grid (collapsible) */}
                      {expandedKraIds.includes(k.id) && (
                        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {k.metrics.map((m) => (
                            <div key={m.id} className="bg-gray-900/60 p-3 rounded-md flex flex-col">
                              <div className="flex items-start justify-between gap-2">
                                <div>
                                  <div className="text-sm text-gray-300 font-semibold">{m.name}</div>
                                  <div className="text-xs text-gray-400">{m.value} / {m.target} {m.unit}</div>
                                </div>
                                <div className="text-right">
                                  <div className="text-sm text-gray-400">Progress</div>
                                  <div className="text-sm font-bold text-blue-300">
                                    {Math.min(100, Math.round((m.value / m.target) * 100))}%
                                  </div>
                                </div>
                              </div>

                              <div className="w-full h-2 bg-gray-800 rounded mt-3 overflow-hidden">
                                <div 
                                  className="h-2 bg-green-500" 
                                  style={{ width: `${Math.min(100, Math.round((m.value / m.target) * 100))}%` }} 
                                />
                              </div>

                              {/* Inline edit controls for metric */}
                              <div className="mt-3 grid grid-cols-2 gap-2">
                                <input
                                  type="number"
                                  min={0}
                                  value={m.value}
                                  onChange={(e) => updateMetric(goal.id, k.id, m.id, Number(e.target.value))}
                                  className="bg-gray-800/50 p-2 rounded-md text-sm outline-none"
                                />
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => {
                                      const newVal = Math.min(m.target, Math.round(m.value * 1.1));
                                      updateMetric(goal.id, k.id, m.id, newVal);
                                    }}
                                    className="flex-1 bg-blue-600 hover:bg-blue-700 py-2 rounded-md text-sm"
                                  >
                                    +10%
                                  </button>
                                  <button
                                    onClick={() => {
                                      const newVal = Math.max(0, Math.round(m.value * 0.9));
                                      updateMetric(goal.id, k.id, m.id, newVal);
                                    }}
                                    className="bg-gray-700 hover:bg-gray-600 py-2 rounded-md text-sm px-3"
                                  >
                                    -10%
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Action buttons for goal */}
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <button
                    onClick={() => toggleGoal(goal.id)}
                    className="col-span-1 sm:col-span-2 bg-blue-600 hover:bg-blue-700 py-2 rounded-md flex items-center justify-center gap-2"
                  >
                    <FontAwesomeIcon icon={faArrowRight} /> {expandedGoalId === goal.id ? 'Collapse' : 'View Details'}
                  </button>

                  <button
                    onClick={() => openModal(goal)}
                    className="bg-blue-600 hover:bg-blue-700 p-2 rounded-md"
                  >
                    <FontAwesomeIcon icon={faEdit} />
                  </button>
                  <button 
                    onClick={() => removeGoal(goal.id)}
                    className="bg-red-600 hover:bg-red-700 p-2 rounded-md"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Goal Modal (Add/Edit) */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-xl w-full max-w-xl p-5 border border-gray-800">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">
                {isEditing ? "Edit Goal" : "Add New Goal (Demo)"}
              </h3>
              <button onClick={() => setShowModal(false)} className="p-2 bg-gray-800 rounded-md">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>

            <div className="space-y-3">
              <input
                value={currentGoal.title}
                onChange={(e) => updateCurrentGoalField("title", e.target.value)}
                placeholder="Goal title"
                className="w-full p-3 bg-gray-800 rounded-md outline-none text-sm"
              />
              <input
                value={currentGoal.quarter}
                onChange={(e) => updateCurrentGoalField("quarter", e.target.value)}
                placeholder="Quarter (eg. Q1-2026)"
                className="w-full p-3 bg-gray-800 rounded-md outline-none text-sm"
              />
              <textarea
                value={currentGoal.description}
                onChange={(e) => updateCurrentGoalField("description", e.target.value)}
                placeholder="Description"
                className="w-full p-3 bg-gray-800 rounded-md outline-none text-sm h-24"
              />

              <div className="text-sm text-gray-400">
                (Optional) Add one sample KRA with metric
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input
                  placeholder="KRA title"
                  value={currentGoal.kras[0]?.title || ""}
                  className="p-2 bg-gray-800 rounded-md text-sm"
                  onChange={(e) => updateCurrentKraTitle(e.target.value)}
                />
                <input
                  placeholder="Metric name"
                  value={currentGoal.kras[0]?.metrics?.[0]?.name || ""}
                  className="p-2 bg-gray-800 rounded-md text-sm"
                  onChange={(e) => updateCurrentMetricName(e.target.value)}
                />
              </div>

              <div className="flex items-center justify-end gap-2 mt-3">
                <button 
                  onClick={() => setShowModal(false)} 
                  className="py-2 px-4 bg-gray-700 rounded-md"
                >
                  Cancel
                </button>
                <button 
                  onClick={saveGoal} 
                  className="py-2 px-4 bg-blue-600 rounded-md"
                >
                  {isEditing ? "Update" : "Add"} Goal
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}